import * as React from 'react'
import { BookingField } from './BookingDetails'

type Asset = {
  asset_code: string
  product_id: string
  product_name: string
  sku: string | null
}

type Assignment = {
  ordered: { product_id: string; product_name: string; sku: string | null; quantity: number }
  assigned: Asset[]
  assigned_at: string
}

type Booking = {
  product_id: string
  quantity: number
  products?: { name: string; sku?: string | null }
  equipment_assignment?: Assignment | null
  booking_unit_changes?: Array<{ id: string; created_at: string; previous_units: Asset[]; assigned_units: Asset[] }>
  inventory_allocations?: Array<{
    state: string
    inventory_units: {
      asset_code: string
      product_id: string
      products?: { name: string; sku: string | null }
    } | null
  }>
}

const recordedTime = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London',
})

export function BookingEquipmentAssignment({ booking }: { booking: Booking }) {
  const record = booking.equipment_assignment
  const ordered = record?.ordered ?? {
    product_id: booking.product_id,
    product_name: booking.products?.name || 'Booked product',
    sku: booking.products?.sku,
    quantity: booking.quantity,
  }
  // Older/automatic bookings have live allocations but no staff-assignment snapshot.
  const assigned: Asset[] = booking.inventory_allocations === undefined ? record?.assigned ?? [] : booking.inventory_allocations.flatMap(allocation => {
    const unit = allocation.inventory_units
    if (!['confirmed', 'returned'].includes(allocation.state) || !unit) return []
    return [{
      asset_code: unit.asset_code,
      product_id: unit.product_id,
      product_name: unit.products?.name || 'Equipment',
      sku: unit.products?.sku || null,
    }]
  })
  const hasSubstitution = assigned.some(asset => asset.product_id !== ordered.product_id)

  return <section className="booked-equipment" aria-label="Ordered and assigned equipment">
    <dl className="booking-fields booking-equipment">
      <BookingField label="Provided units">{!assigned.length ? 'Not assigned' : assigned.map(asset => <div key={asset.asset_code}>
        <strong>{asset.asset_code}</strong><small>{asset.product_name}{asset.sku ? ` · ${asset.sku}` : ''}{asset.product_id !== ordered.product_id && ' · Substitute model'}</small>
      </div>)}</BookingField>
      <BookingField label="Ordered unit"><strong>{ordered.quantity} × {ordered.product_name}</strong>{ordered.sku && <small>{ordered.sku}</small>}</BookingField>
    </dl>
    {hasSubstitution && <p className="message warning">The assigned model differs from the original order. The customer’s order and price are unchanged.</p>}
    {(record || !!booking.booking_unit_changes?.length) && <details><summary>Assignment history</summary>
    {record && <div>
      <p><strong>Originally assigned:</strong> {record.assigned.map(asset => `${asset.asset_code} (${asset.product_name})`).join(', ') || 'Not assigned'}</p>
      <small>Original assignment recorded {recordedTime.format(new Date(record.assigned_at))}</small>
    </div>}
    {!!booking.booking_unit_changes?.length && <div>
      <h4>Unit change history</h4>
      <ul>{[...booking.booking_unit_changes].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id)).map(change => <li key={change.id}>
        {recordedTime.format(new Date(change.created_at))}: {change.previous_units.map(asset => `${asset.asset_code} (${asset.product_name})`).join(', ')} → {change.assigned_units.map(asset => `${asset.asset_code} (${asset.product_name})`).join(', ')}
      </li>)}</ul>
    </div>}
    </details>}
  </section>
}
